<template>
    <div id="app">
    <div class="task-manager">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center app-header">
        <h1 class="m-0 app-title">Task Manager</h1>
        <div class="d-flex">
          <b-button-group class="mr-3">
            <b-button variant="outline-secondary" class="px-3"><i class="fas fa-th"></i></b-button>
            <b-button variant="outline-secondary" class="px-3"><i class="fas fa-list"></i></b-button>
          </b-button-group>
          
          <b-input-group>
            <b-form-input placeholder="Search tasks..." v-model="searchQuery" class="border-right-0"></b-form-input>
            <b-dropdown text="All Priorities" class="ml-2" right>
              <b-dropdown-item v-for="option in priorityOptions" :key="option.value" :active="selectedPriority === option.value" @click="selectedPriority = option.value">
                {{ option.text }}
              </b-dropdown-item>
            </b-dropdown>
            
            <b-dropdown text="Sort by Due Date" class="ml-2" right>
              <b-dropdown-item v-for="option in sortOptions" :key="option.value" :active="selectedSort === option.value" @click="selectedSort = option.value">
                {{ option.text }}
              </b-dropdown-item>
            </b-dropdown>
            
            <b-button variant="link" class="ml-2"><i class="fas fa-arrow-up"></i></b-button>
          </b-input-group>
        </div>
      </div>
      
      <!-- Add Task Form -->
      <b-card class="form-card">
        <b-form @submit.prevent="addNewTask">
          <b-form-input 
            v-model="newTask.title" 
            placeholder="Add a new task..." 
            class="mb-4 border-0 shadow-sm"
            style="font-size: 16px; padding: 14px; background-color: #f5f7ff;"
          ></b-form-input>
          
          <div class="d-flex flex-wrap">
            <div class="mr-4 mb-3">
              <label>Due Date</label>
              <b-form-datepicker v-model="newTask.dueDate" placeholder="Choose date" class="bg-white"></b-form-datepicker>
            </div>
            
            <div class="mr-4 mb-3">
              <label>Time</label>
              <b-form-timepicker v-model="newTask.dueTime" placeholder="Choose time" class="bg-white"></b-form-timepicker>
            </div>
            
            <div class="mr-4 mb-3" style="min-width: 150px;">
              <label>Priority</label>
              <b-form-select v-model="newTask.priority" :options="priorityOptions" class="bg-white"></b-form-select>
            </div>
            
            <div class="mr-4 mb-3">
              <label>Assign To</label>
              <div class="d-flex align-items-center">
                <div v-for="member in selectedMembers" :key="member.id" class="mr-2">
                  <span class="user-avatar" :class="'avatar-' + member.initial.toLowerCase()">
                    {{ member.initial }}
                  </span>
                  <b-button size="sm" variant="link" class="p-0" @click="removeMember(member)">
                    <i class="fas fa-times-circle text-muted"></i>
                  </b-button>
                </div>
                
                <b-dropdown text="Add member..." right variant="outline-secondary" class="ml-2">
                  <b-dropdown-item v-for="member in availableMembers" 
                                   :key="member.id" 
                                   @click="addMember(member)">
                    <div class="d-flex align-items-center">
                      <span class="user-avatar mr-2" :class="'avatar-' + member.initial.toLowerCase()" style="width: 24px; height: 24px; font-size: 10px;">
                        {{ member.initial }}
                      </span>
                      {{ member.name }}
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end mt-3">
            <b-button variant="primary" type="submit" class="px-4">
              <i class="fas fa-plus mr-2"></i> Add Task
            </b-button>
          </div>
        </b-form>
      </b-card>
      
      <!-- Task Sections -->
      <b-row>
        <!-- My Tasks -->
        <b-col lg="4" md="6" class="mb-4">
          <div class="task-section">
            <div class="task-header">
              <i class="fas fa-tasks my-tasks-icon"></i> My Tasks
            </div>
            
            <div v-if="myTasks.length === 0" class="empty-state">
              <i class="far fa-clipboard mb-2" style="font-size: 24px;"></i>
              <div>No tasks assigned to you</div>
            </div>
            
            <b-card v-for="task in myTasks" 
                    :key="task.id" 
                    class="task-card"
                    no-body>
              <b-card-body>
                <div class="d-flex justify-content-between align-items-start">
                  <h5 class="mb-1">{{ task.title }}</h5>
                  <b-button variant="link" class="p-0 completion-toggle" @click="toggleTaskComplete(task)">
                    <i :class="['far', task.completed ? 'fa-check-circle completed-task' : 'fa-circle']"></i>
                  </b-button>
                </div>
                
                <div class="d-flex mt-2 task-meta">
                  <div class="mr-3">
                    <i class="far fa-calendar-alt"></i>
                    {{ formatDate(task.dueDate) }}
                  </div>
                  <div>
                    <i class="far fa-clock"></i>
                    {{ formatTime(task.dueTime) }}
                  </div>
                </div>
                
                <div class="d-flex justify-content-between align-items-center mt-3">
                  <div v-if="task.priority">
                    <b-badge variant="warning" class="badge-task-medium">{{ task.priority }}</b-badge>
                  </div>
                  
                  <div>
                    <span v-for="member in task.assignees" :key="member.id" class="user-avatar" :class="'avatar-' + member.initial.toLowerCase()">
                      {{ member.initial }}
                    </span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </b-col>
        
        <!-- Created by Me -->
        <b-col lg="4" md="6" class="mb-4">
          <div class="task-section">
            <div class="task-header">
              <i class="fas fa-pencil-alt created-tasks-icon"></i> Created by Me
              <b-badge variant="primary" pill class="ml-2">{{ createdTasks.length }}</b-badge>
            </div>
            
            <b-card v-for="task in createdTasks" 
                    :key="task.id" 
                    class="task-card"
                    no-body>
              <b-card-body>
                <div class="d-flex justify-content-between align-items-start">
                  <h5 class="mb-1">{{ task.title }}</h5>
                  <b-button variant="link" class="p-0 completion-toggle" @click="toggleTaskComplete(task)">
                    <i :class="['far', task.completed ? 'fa-check-circle completed-task' : 'fa-circle']"></i>
                  </b-button>
                </div>
                
                <div class="d-flex mt-2 task-meta">
                  <div class="mr-3">
                    <i class="far fa-calendar-alt"></i>
                    {{ formatDate(task.dueDate) }}
                  </div>
                  <div>
                    <i class="far fa-clock"></i>
                    {{ formatTime(task.dueTime) }}
                  </div>
                </div>
                
                <div class="d-flex justify-content-between align-items-center mt-3">
                  <div v-if="task.priority">
                    <b-badge variant="warning" class="badge-task-medium">{{ task.priority }}</b-badge>
                  </div>
                  
                  <div>
                    <span v-for="member in task.assignees" :key="member.id" class="user-avatar" :class="'avatar-' + member.initial.toLowerCase()">
                      {{ member.initial }}
                    </span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </b-col>
        
        <!-- Team Tasks -->
        <b-col lg="4" md="12" class="mb-4">
          <div class="task-section">
            <div class="task-header">
              <i class="fas fa-users team-icon"></i> Team Tasks
            </div>
            
            <div v-if="teamTasks.length === 0" class="empty-state">
              <i class="fas fa-users mb-2" style="font-size: 24px;"></i>
              <div>No team tasks available</div>
            </div>
            
            <b-card v-for="task in teamTasks" 
                    :key="task.id" 
                    class="task-card"
                    no-body>
              <b-card-body>
                <div class="d-flex justify-content-between align-items-start">
                  <h5 class="mb-1">{{ task.title }}</h5>
                  <b-button variant="link" class="p-0 completion-toggle" @click="toggleTaskComplete(task)">
                    <i :class="['far', task.completed ? 'fa-check-circle completed-task' : 'fa-circle']"></i>
                  </b-button>
                </div>
                
                <div class="d-flex mt-2 task-meta">
                  <div class="mr-3">
                    <i class="far fa-calendar-alt"></i>
                    {{ formatDate(task.dueDate) }}
                  </div>
                  <div>
                    <i class="far fa-clock"></i>
                    {{ formatTime(task.dueTime) }}
                  </div>
                </div>
                
                <div class="d-flex justify-content-between align-items-center mt-3">
                  <div v-if="task.priority">
                    <b-badge variant="warning" class="badge-task-medium">{{ task.priority }}</b-badge>
                  </div>
                  
                  <div>
                    <span v-for="member in task.assignees" :key="member.id" class="user-avatar" :class="'avatar-' + member.initial.toLowerCase()">
                      {{ member.initial }}
                    </span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
  </template>
  
  <script>
  export default {
    data() {
      return {
        searchQuery: '',
          selectedPriority: 'all',
          selectedSort: 'dueDate',
          taskIdCounter: 3,
          newTask: {
            title: '',
            dueDate: '',
            dueTime: '',
            priority: 'Medium',
            assignees: []
          },
          selectedMembers: [],
          tasks: [
            {
              id: 1,
              title: 'Lorem ipsum is simply dummy text of',
              dueDate: '2025-02-28',
              dueTime: '09:00:00',
              priority: 'Medium',
              completed: true,
              createdBy: 'currentUser',
              assignees: []
            },
            {
              id: 2,
              title: 'tst2',
              dueDate: '2025-03-04',
              dueTime: '09:00:00',
              priority: 'Medium',
              completed: false,
              createdBy: 'currentUser',
              assignees: [
                { id: 1, name: 'Alice', initial: 'A' },
                { id: 2, name: 'Charlie', initial: 'C' }
              ]
            }
          ],
          members: [
            { id: 1, name: 'Alice', initial: 'A' },
            { id: 2, name: 'Charlie', initial: 'C' },
            { id: 3, name: 'Bob', initial: 'B' },
            { id: 4, name: 'David', initial: 'D' }
          ],
          priorityOptions: [
            { value: 'all', text: 'All Priorities' },
            { value: 'High', text: 'High' },
            { value: 'Medium', text: 'Medium' },
            { value: 'Low', text: 'Low' }
          ],
          sortOptions: [
            { value: 'dueDate', text: 'Sort by Due Date' },
            { value: 'priority', text: 'Sort by Priority' },
            { value: 'title', text: 'Sort by Title' }
          ]
        }      
    },
    
    computed: {
        myTasks() {
          // Filter tasks assigned to current user (simplified)
          return this.filteredTasks.filter(task => 
            task.assignees.some(member => member.id === 1)
          );
        },
        createdTasks() {
          // Filter tasks created by current user
          return this.filteredTasks.filter(task => task.createdBy === 'currentUser');
        },
        teamTasks() {
          // All team tasks (could be filtered by team in a real app)
          return [];
        },
        filteredTasks() {
          let filtered = [...this.tasks];
          
          // Apply search filter
          if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter(task => 
              task.title.toLowerCase().includes(query)
            );
          }
          
          // Apply priority filter
          if (this.selectedPriority !== 'all') {
            filtered = filtered.filter(task => 
              task.priority === this.selectedPriority
            );
          }
          
          // Apply sorting
          filtered.sort((a, b) => {
            if (this.selectedSort === 'dueDate') {
              return new Date(a.dueDate + ' ' + a.dueTime) - new Date(b.dueDate + ' ' + b.dueTime);
            } else if (this.selectedSort === 'priority') {
              const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
              return priorityOrder[a.priority] - priorityOrder[b.priority];
            } else if (this.selectedSort === 'title') {
              return a.title.localeCompare(b.title);
            }
            return 0;
          });
          
          return filtered;
        },
        availableMembers() {
          // Filter out already selected members
          return this.members.filter(member => 
            !this.selectedMembers.some(selected => selected.id === member.id)
          );
        }
    },
    
    methods: {
        addNewTask() {
          if (!this.newTask.title.trim()) {
            this.$bvToast.toast('Please enter a task title', {
              title: 'Warning',
              variant: 'warning',
              solid: true
            });
            return;
          }
          
          const task = {
            id: this.taskIdCounter++,
            title: this.newTask.title,
            dueDate: this.newTask.dueDate || this.formatCurrentDate(),
            dueTime: this.newTask.dueTime || '09:00:00',
            priority: this.newTask.priority,
            completed: false,
            createdBy: 'currentUser',
            assignees: [...this.selectedMembers]
          };
          
          this.tasks.push(task);
          
          // Show success toast
          this.$bvToast.toast('Task has been added successfully', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
          
          // Reset form
          this.newTask = {
            title: '',
            dueDate: '',
            dueTime: '',
            priority: 'Medium',
            assignees: []
          };
          this.selectedMembers = [];
        },
        toggleTaskComplete(task) {
          task.completed = !task.completed;
          
          // Show success toast
          this.$bvToast.toast(`Task marked as ${task.completed ? 'completed' : 'incomplete'}`, {
            title: 'Status Updated',
            variant: 'info',
            solid: true
          });
        },
        formatDate(dateString) {
          if (!dateString) return '';
          const date = new Date(dateString);
          return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
        },
        formatTime(timeString) {
          if (!timeString) return '';
          const [hours, minutes] = timeString.split(':');
          return `${hours}:${minutes} AM`;
        },
        formatCurrentDate() {
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        },
        addMember(member) {
          this.selectedMembers.push(member);
        },
        removeMember(member) {
          this.selectedMembers = this.selectedMembers.filter(m => m.id !== member.id);
        }
    }
  }
  </script>
  
  <style>
  :root {
      --primary-color: #5c6bc0;
      --secondary-color: #7986cb;
      --accent-color: #3949ab;
      --light-color: #f5f7ff;
      --medium-color: #e8eaf6;
      --dark-color: #303f9f;
      --text-primary: #363636;
      --text-secondary: #616161;
      --text-light: #9e9e9e;
      --success-color: #4caf50;
      --warning-color: #ff9800;
      --danger-color: #f44336;
      --radius-sm: 6px;
      --radius-md: 10px;
      --radius-lg: 16px;
      --shadow-sm: 0 2px 8px rgba(0,0,0,0.08);
      --shadow-md: 0 4px 12px rgba(0,0,0,0.1);
      --shadow-lg: 0 6px 16px rgba(0,0,0,0.12);
      --transition: all 0.2s ease-in-out;
    }
    
    body {
      background-color: #f8f9fa;
      font-family: 'Inter', sans-serif;
      color: var(--text-primary);
      line-height: 1.6;
      padding-bottom: 40px;
    }
    
    h1, h2, h3, h4, h5, h6 {
      font-weight: 600;
    }
    
    .task-manager {
      max-width: 1200px;
      margin: 30px auto;
    }
    
    /* Header styling */
    .app-header {
      margin-bottom: 32px;
    }
    
    .app-title {
      font-weight: 700;
      font-size: 28px;
      background: linear-gradient(to right, var(--primary-color), var(--dark-color));
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      letter-spacing: -0.5px;
    }
    
    /* Button styling */
    .btn-primary {
      background: var(--primary-color);
      border-color: var(--primary-color);
      border-radius: var(--radius-sm);
      font-weight: 500;
      padding: 8px 16px;
      transition: var(--transition);
      box-shadow: 0 2px 5px rgba(92, 107, 192, 0.2);
    }
    
    .btn-primary:hover, .btn-primary:focus {
      background: var(--dark-color);
      border-color: var(--dark-color);
      box-shadow: 0 4px 8px rgba(92, 107, 192, 0.3);
      transform: translateY(-1px);
    }
    
    .btn-outline-secondary {
      border-color: #e0e0e0;
      color: var(--text-secondary);
    }
    
    .btn-outline-secondary:hover, .btn-outline-secondary:focus {
      background-color: var(--light-color);
      color: var(--primary-color);
      border-color: var(--primary-color);
    }
    
    /* Form styling */
    .form-card {
      background-color: white;
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-md);
      border: none;
      overflow: hidden;
      margin-bottom: 32px;
    }
    
    .form-card .card-body {
      padding: 24px;
    }
    
    .form-control {
      border-radius: var(--radius-sm);
      border: 1px solid #e0e0e0;
      padding: 12px;
      height: auto;
      transition: var(--transition);
    }
    
    .form-control:focus {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(92, 107, 192, 0.15);
    }
    
    label {
      font-weight: 500;
      font-size: 14px;
      color: var(--text-secondary);
      margin-bottom: 8px;
    }
    
    .b-form-datepicker, .b-form-timepicker, .custom-select {
      border-radius: var(--radius-sm) !important;
    }
    
    /* Task sections styling */
    .task-section {
      background-color: white;
      border-radius: var(--radius-lg);
      padding: 24px;
      height: 100%;
      box-shadow: var(--shadow-sm);
      position: relative;
      overflow: hidden;
      transition: var(--transition);
    }
    
    .task-section:hover {
      box-shadow: var(--shadow-md);
    }
    
    .task-section::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
    }
    
    .task-header {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      font-weight: 600;
      font-size: 18px;
      color: var(--text-primary);
    }
    
    .task-header i {
      margin-right: 12px;
      font-size: 20px;
    }
    
    .team-icon {
      color: #4caf50;
    }
    
    .my-tasks-icon {
      color: #2196f3;
    }
    
    .created-tasks-icon {
      color: #9c27b0;
    }
    
    /* Task cards styling */
    .task-card {
      margin-bottom: 16px;
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-sm);
      border: 1px solid #f0f0f0;
      transition: var(--transition);
      overflow: hidden;
    }
    
    .task-card:hover {
      box-shadow: var(--shadow-md);
      transform: translateY(-2px);
    }
    
    .task-card .card-body {
      padding: 16px;
    }
    
    .task-card h5 {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.4;
      margin-bottom: 0;
      color: var(--text-primary);
    }
    
    .badge-task-medium {
      background-color: var(--warning-color);
      color: white;
      font-weight: 500;
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 12px;
    }
    
    .badge-primary {
      background-color: var(--primary-color);
      padding: 4px 10px;
      font-weight: 500;
      font-size: 12px;
    }
    
    .empty-state {
      color: var(--text-light);
      text-align: center;
      padding: 30px 20px;
      font-size: 15px;
      background-color: var(--light-color);
      border-radius: var(--radius-md);
      border: 1px dashed #e0e0e0;
    }
    
    /* User avatars */
    .user-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 12px;
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      margin-right: 5px;
    }
    
    .avatar-a {
      background: linear-gradient(135deg, #ff5252, #ff1744);
    }
    
    .avatar-c {
      background: linear-gradient(135deg, #2979ff, #2962ff);
    }
    
    .avatar-b {
      background: linear-gradient(135deg, #00c853, #00b248);
    }
    
    .avatar-d {
      background: linear-gradient(135deg, #aa00ff, #7200ca);
    }
    
    /* Task meta styling */
    .task-meta {
      display: flex;
      align-items: center;
      color: var(--text-light);
      font-size: 13px;
    }
    
    .task-meta i {
      margin-right: 4px;
    }
    
    /* Completion toggle */
    .completed-task {
      color: var(--success-color);
    }
    
    .completion-toggle {
      font-size: 18px;
      color: #bdbdbd;
      transition: var(--transition);
    }
    
    .completion-toggle:hover {
      color: var(--success-color);
    }
    
    /* Responsive adjustments */
    @media (max-width: 992px) {
      .task-section {
        margin-bottom: 24px;
      }
    }
    
    @media (max-width: 768px) {
      .app-title {
        margin-bottom: 16px;
      }
      
      .form-section {
        flex-direction: column;
      }
      
      .form-group {
        width: 100%;
        margin-bottom: 16px;
      }
    }
  </style>